import { JSX, useActionState, useState } from "react";
import { Checkbox } from "@shared/EDS/components/Checkbox";
import { Duration } from "luxon";
import { Input } from "@shared/EDS/components/Input";
import { GenerateSolutionRequest } from "@opstel/gen/knop/ui/v1/api/hip_pb";
import { DATETIME_FORMAT_NO_SECONDS, durationToProto } from "@shared/common/utils/luxonHelpers";
import { Scenario } from "@opstel/query/api/scenario";

interface GenerateSolutionContentProps {
  onCancel?: () => void;
  onGenerateSolution: (data: GenerateSolutionRequest) => void;
  instanceMetadata: Scenario;
}

interface FormState {
  instanceId: string;
  stopWhenFeasible: boolean;
  maxComputationTimeHours: number;
  maxComputationTimeMinutes: number;
  message: string;
}

function parseFormData(formData: FormState): GenerateSolutionRequest {
  return new GenerateSolutionRequest({
    instanceId: formData.instanceId,
    stopWhenFeasible: formData.stopWhenFeasible,
    maximumComputationTime: durationToProto(
      Duration.fromObject({
        hours: formData.maxComputationTimeHours,
        minutes: formData.maxComputationTimeMinutes,
      }),
    ),
  });
}

export function GenerateSolutionContent({
  onCancel,
  onGenerateSolution,
  instanceMetadata,
}: Readonly<GenerateSolutionContentProps>): JSX.Element {
  const [stopWhenFeasible, setStopWhenFeasible] = useState(true);

  const [_state, formAction, _isPending] = useActionState<FormState, FormData>(action, {
    instanceId: instanceMetadata?.instanceId ?? "",
    maxComputationTimeHours: 0,
    maxComputationTimeMinutes: 30,
    stopWhenFeasible: true,
    message: "",
  });

  function action(prevState: FormState, queryData: FormData): FormState {
    const newState: FormState = {
      ...prevState,
      maxComputationTimeHours: Number(queryData.get("maxComputationTimeHours")),
      maxComputationTimeMinutes: Number(queryData.get("maxComputationTimeMinutes")),
      stopWhenFeasible:
        queryData.has("stopWhenFeasible") && queryData.get("stopWhenFeasible") === "on",
    };

    const generateSolutionData = parseFormData(newState);
    onGenerateSolution(generateSolutionData);
    return newState;
  }

  function toggleStopWhenFeasible() {
    setStopWhenFeasible((prev) => !prev);
  }

  return (
    <form
      action={formAction}
      className="grid w-full grid-cols-[1fr_3fr] items-center gap-m px-l py-0"
    >
      <label htmlFor="knoop" className="text-s leading-l font-medium text-subtle">
        Knoop
      </label>
      <span data-name="knoop">{instanceMetadata?.location}</span>

      <label htmlFor="periode" className="text-s leading-l font-medium text-subtle">
        Periode
      </label>
      <span data-name="periode">
        {instanceMetadata?.scenarioStart.toFormat(DATETIME_FORMAT_NO_SECONDS)} t/m{" "}
        {instanceMetadata?.scenarioEnd.toFormat(DATETIME_FORMAT_NO_SECONDS)}
      </span>

      <label htmlFor="rekentijd" className="text-s leading-l font-medium text-subtle">
        Rekentijd
      </label>
      <div className="flex items-center gap-m [&>span]:mr-m">
        <Input
          data-testid="generate-solution-hours-input"
          type="number"
          name="maxComputationTimeHours"
          aria-label="Rekentijd (Uren)"
          defaultValue={0}
          min={0}
        />
        <span>uur</span>
        <Input
          data-testid="generate-solution-minutes-input"
          type="number"
          name="maxComputationTimeMinutes"
          aria-label="Rekentijd (Minuten)"
          defaultValue={30}
          min={0}
          max={60}
        />
        <span>min</span>
      </div>

      {/* Spacer to push checkbox to next column */}
      <div />
      <Checkbox
        data-testid="generate-solution-stop-when-feasible-checkbox"
        name="stopWhenFeasible"
        isChecked={stopWhenFeasible}
        onCheckedChange={() => toggleStopWhenFeasible()}
        label="Stop wanneer oplossing conflictvrij is"
      />

      <div className="col-start-2 flex items-end justify-end gap-m">
        <tractie-button
          data-testid="generate-solution-cancel-button"
          level="tertiary"
          onClick={onCancel}
        >
          Annuleren
        </tractie-button>
        <tractie-button-submit
          data-testid="generate-solution-start-button"
          level="primary"
          autoFocus
        >
          Starten
        </tractie-button-submit>
      </div>
    </form>
  );
}
